import { Button } from 'base/ui/button';
import { IconCopyAi, IconLock, IconShuffleAi, IconStars } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { Locale } from 'base/ui/locales';
import { DividerLine } from 'base/ui/panel';
import { PromptTextarea, Textarea } from 'base/ui/textarea';
import { ToastQueue } from 'base/ui/toast';
import { TextSmallLight, TextTinyLight } from 'base/ui/typography';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { Assistant } from '../ai/assistant';
import {
  getIsPageDescriptionRequired,
  getPageContextError,
  getPageDescriptionError,
  getPageNameError,
  LimitCounter,
  PAGE_DESCRIPTION_LIMIT,
  PAGE_NAME_LIMIT,
} from '../ai/validation';
import { getGlobalDesignStore } from '../design/design_store';
import { PageEditingActions } from '../editing_actions/page';
import {
  EditorMode,
  editorModeStore,
  isWireframeOrDesign,
} from '../editor_mode';
import { Page } from '../model';
import { SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import { FormEmptyState, RequiredStar } from './ui';

const PRIMARY_AI_VARIANT = { variant: 'primary', background: 'ai' } as const;
const SECONDARY_AI_VARIANT = { variant: 'secondary', iconColor: 'ai' } as const;
const PATH_LABEL = 'Name';

export const PageGenerateForm = observer(function _PageGenerateForm({
  page,
  pageEditing,
  locale,
  assistant,
  selection,
  session,
  toastQueue,
}: {
  page: Page;
  pageEditing: PageEditingActions;
  locale: Locale;
  assistant: Assistant;
  selection: SelectionStore;
  session: ProjectSession;
  toastQueue: ToastQueue;
}) {
  const { project } = selection;
  const refName = React.useRef<HTMLTextAreaElement>(null);
  const refDescription = React.useRef<HTMLTextAreaElement>(null);

  const pageEmpty = page.sections.length === 0;
  const inWireframeMode = isWireframeOrDesign(editorModeStore.get());

  const pageNameError = getPageNameError(page.name);
  const pageDescriptionError = getPageDescriptionError(page.description);
  const { space } = selection.factory.forPage(page);
  const contextError = getPageContextError(page.state, space, project.state);
  const error = pageNameError || pageDescriptionError || contextError;
  const pageDescriptionRequired = getIsPageDescriptionRequired(space, project);

  if (inWireframeMode && !session.canViewWireframe(page)) {
    return (
      <FormEmptyState
        icon={IconLock}
        buttonLabel="Upgrade"
        onClick={() => session.showUpgradeWall?.('pages')}
      >
        To unlock wireframes for all pages, you need to upgrade.
      </FormEmptyState>
    );
  }

  const isGenerating = assistant.taskLog.affectedByPending.has(page);

  return (
    <Rows spacing={16}>
      <Rows spacing={8}>
        <TextTinyLight color="grey">
          Name
          <RequiredStar />
        </TextTinyLight>
        <TextSmallLight>
          <Textarea
            ref={refName}
            value={page.name}
            lang={locale}
            onChange={pageEditing.setName}
            placeholder="Page Name"
          />
        </TextSmallLight>
        <LimitCounter value={page.name} limit={PAGE_NAME_LIMIT} />
      </Rows>

      <Rows spacing={8}>
        <TextTinyLight color="grey">
          Description
          {pageDescriptionRequired && <RequiredStar />}
        </TextTinyLight>
        <TextSmallLight>
          <PromptTextarea
            ref={refDescription}
            value={page.description}
            lang={locale}
            onChange={pageEditing.setDescription}
            placeholder="Add a unique description to regenerate the page with a new layout and copy..."
            isInvalid={pageDescriptionError != null}
            minHeight={120}
            allowNewlines
          />
        </TextSmallLight>
        <LimitCounter value={page.description} limit={PAGE_DESCRIPTION_LIMIT} />
      </Rows>

      {inWireframeMode && !isGenerating && !pageEmpty && (
        <Rows spacing={8}>
          <Button
            label="Shuffle components"
            icon={IconShuffleAi}
            {...SECONDARY_AI_VARIANT}
            disabled={error != null}
            tooltip={error}
            onClick={() =>
              assistant.chooseComponents(page, {
                replaceExistingComponents: true,
                generateCopy: inWireframeMode,
              })
            }
          />
          <Button
            label="Generate copy"
            icon={IconCopyAi}
            {...SECONDARY_AI_VARIANT}
            disabled={error != null}
            tooltip={error}
            onClick={() => {
              session.analytics.track({
                event: 'project:copy_generate',
                copy_generate_scope: 'page',
                has_copy: pageEditing.resetCopy != null,
              });
              assistant.generateCopy(
                new Map(page.sections.map((sref) => [sref, undefined])),
              );
            }}
          />
        </Rows>
      )}

      {editorModeStore.get() === EditorMode.Design && (
        <>
          <DividerLine />
          <Button
            label="Reapply schemes"
            icon={IconShuffleAi}
            {...SECONDARY_AI_VARIANT}
            onClick={() => {
              const concept = getGlobalDesignStore().design.linkedConcept;
              assistant.applyDesignSchemesToPages([page], concept);
            }}
          />
        </>
      )}

      <Rows spacing={16}>
        <DividerLine expandToEdges={true} />
        <Rows spacing={8}>
          <Button
            label={isGenerating ? 'Generating...' : 'Generate page'}
            icon={IconStars}
            {...PRIMARY_AI_VARIANT}
            disabled={isGenerating || error != null}
            tooltip={error}
            onClick={() => {
              const designOpts =
                editorModeStore.get() === EditorMode.Design
                  ? {
                      applyDesignSchemes: true as const,
                      concept: getGlobalDesignStore().design.linkedConcept,
                    }
                  : {};

              assistant.generatePages([page], {
                chooseComponents: inWireframeMode,
                generateCopy: inWireframeMode,
                ...designOpts,
              });
            }}
          />
          {!pageEmpty && (
            <TextTinyLight align="center" color="grey">
              This will override all page sections and copy
            </TextTinyLight>
          )}
        </Rows>
      </Rows>
    </Rows>
  );
});

export const PathForm = observer(function _PageGenerateForm({
  page,
  pageEditing,
  locale,
}: {
  page: Page;
  pageEditing: PageEditingActions;
  locale: Locale;
}) {
  const ref = React.useRef<HTMLTextAreaElement>(null);
  return (
    <Rows spacing={8}>
      <TextTinyLight color="grey">
        {PATH_LABEL}
        <RequiredStar />
      </TextTinyLight>
      <TextSmallLight>
        <Textarea
          ref={ref}
          value={page.name}
          lang={locale}
          onChange={pageEditing.setName}
          placeholder={PATH_LABEL}
        />
      </TextSmallLight>
      <LimitCounter value={page.name} limit={PAGE_NAME_LIMIT} />
    </Rows>
  );
});
